import type { ReactNode } from "react";
import { cn } from "./cn";

/**
 * ETKİN FİLTRE ŞERİDİ — G-UYG-10
 *
 * ================================================================
 * BOŞ LİSTENİN İKİ SEBEBİ VAR VE EKRAN İKİSİNİ AYIRMIYORDU
 * ================================================================
 * Filtreli bir liste boş dönerse kullanıcı iki gerçeği ayırt edemez:
 * kayıt yoktur, ya da filtre onu eliyordur. İkincisinde kullanıcı
 * OLMAYAN bir veriyi aramaya devam eder — üstelik aradığı veri
 * oradadır, yalnızca bir tarih aralığının dışındadır.
 *
 * Bu şerit üç şeyi birden söylüyor: kaç filtre etkin, HANGİLERİ, ve
 * hepsini tek hamlede nasıl kaldıracağı.
 *
 * ================================================================
 * ÇİPLER TEK TEK KALDIRILABİLİR — VE BU "TEMİZLE"DEN FARKLI
 * ================================================================
 * "Filtreleri temizle" hepsini siler; çipteki `×` yalnızca onu. Fark
 * gündelik kullanımda büyük: dört filtre kurup beşincinin fazla
 * olduğunu anlayan kullanıcı, ötekileri baştan kurmak zorunda kalmıyor.
 *
 * `onKaldir` verilmeyen filtre çipi `×` çizmiyor: kaldırılamayan bir
 * şeye kaldırma düğmesi koymak, basınca hiçbir şey olmayan bir düğme
 * üretirdi.
 *
 * ================================================================
 * HİÇ FİLTRE YOKKEN ŞERİT ÇİZİLMİYOR
 * ================================================================
 * "0 filtre etkin" bir bilgi değil, gürültü. Ama `bilgi` verilmişse
 * (liste kesildi gibi) şerit yine çiziliyor — o cümlenin filtreyle
 * ilgisi yok ve filtresiz listede de doğru.
 */

export interface EtkinFiltre {
  /** React anahtarı ve ekran okuyucu için ayırt edici. */
  anahtar: string;
  /** Kullanıcının gördüğü özet — "01.08 – 08.08.2026", "Kategori: SECURITY" */
  etiket: string;
  /** Yalnızca bu filtreyi kaldırır. Verilmezse çipte `×` çizilmiyor. */
  onKaldir?: () => void;
}

export interface FiltreSeridiProps {
  filtreler: readonly EtkinFiltre[];
  /** Hepsini tek hamlede kaldırır. */
  onTemizle: () => void;
  /**
   * Sağda duran ek bilgi — "1.284 kayıt · ilk 200 gösteriliyor" (G-UYG-11).
   *
   * ================================================================
   * BU YORUM BİR GÜN BOYUNCA YANLIŞTI — DÜZELTİLDİ
   * ================================================================
   * Burada "BUGÜN KULLANILMIYOR, çünkü uçlar gerçek toplam
   * döndürmüyor" yazıyordu. O gün doğruydu; aynı gün sunucu
   * `{ toplam, gosterilen, sinir }` döndürmeye başladı ve alan
   * `Personel` ile `Finans`ta **kullanılmaya başlandı** — yorum
   * olduğu yerde kaldı.
   *
   * Yani bir sonraki okuyan, çalışan bir yolu "henüz yok" sanacaktı.
   * Aynı sınıf: kod değişti, onu anlatan cümle değişmedi.
   *
   * Kullanımı: `listeKesintiMetni(k)` doğrudan buraya verilir;
   * kesilmemiş listede `null` döner ve şerit hiç çizilmez.
   */
  bilgi?: ReactNode;
  className?: string;
}

export function FiltreSeridi({ filtreler, onTemizle, bilgi, className }: FiltreSeridiProps) {
  if (filtreler.length === 0 && !bilgi) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-2 px-0.5", className)}>
      {filtreler.length > 0 && (
        <>
          {/*
            SAYI ONCE: kullanicinin ilk sorusu "kac tane" degil ama
            gozunun ilk yakaladigi sey bu ve cipleri okumadan once
            "filtreli bakiyorum" bilgisini veriyor.
          */}
          <span className="text-notr-900 text-[13px] font-semibold">
            {filtreler.length} filtre etkin
          </span>

          {filtreler.map((f) => (
            <span
              key={f.anahtar}
              className="rounded-rozet bg-marka-50 text-marka-700 ring-marka-200 inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs ring-1 ring-inset"
            >
              {f.etiket}
              {f.onKaldir && (
                <button
                  type="button"
                  onClick={f.onKaldir}
                  className="rounded-rozet text-marka-700 ease-cikis hover:text-marka-600 transition-colors duration-(--sure-anlik)"
                >
                  {/*
                    Gorunen `×` `aria-hidden`; erisilebilir ad TAM
                    cumle. Yirmi filtre cipinde yirmi tane "×"
                    duyurmak, hicbirini ayirt edilebilir yapmiyor.
                  */}
                  <span aria-hidden="true">×</span>
                  <span className="sr-only">{f.etiket} filtresini kaldır</span>
                </button>
              )}
            </span>
          ))}
        </>
      )}

      {bilgi && <span className="text-notr-600 text-[13px]">{bilgi}</span>}

      {filtreler.length > 0 && (
        <button
          type="button"
          onClick={onTemizle}
          className={cn(
            "rounded-alan text-marka-700 ml-auto min-h-[34px] px-3 text-[13px] font-medium",
            "ease-cikis hover:bg-marka-50 transition-colors duration-(--sure-anlik)",
          )}
        >
          Filtreleri temizle
        </button>
      )}
    </div>
  );
}

/**
 * Boş liste metni — G-UYG-10'un üçüncü maddesi.
 *
 * ================================================================
 * NEDEN FONKSİYON, NEDEN EKRANDA ELLE YAZILMIYOR
 * ================================================================
 * Kural şu: filtre etkinken "kayıt yok" YAZILMAZ, "bu filtrelerle
 * kayıt yok" yazılır. Her ekranda elle yazılsaydı biri unuturdu ve
 * unutulduğunda hiçbir şey bağırmazdı — kullanıcı olmayan bir veriyi
 * aramaya devam ederdi (CLAUDE.md kural 10).
 *
 * Burada tek yerde duruyor ve `FiltreSeridi` ile aynı sayıyı alıyor;
 * ikisi ayrışamıyor.
 */
export function bosListeMetni(etkinFiltreSayisi: number): {
  baslik: string;
  aciklama: string;
} {
  if (etkinFiltreSayisi === 0) {
    return {
      baslik: "Kayıt yok",
      aciklama: "Bu listede henüz kayıt bulunmuyor.",
    };
  }
  return {
    baslik: "Bu filtrelerle kayıt yok",
    /*
      CUMLE FILTRENIN KENDISINI SUCLUYOR ve ne yapilacagini soyluyor.
      "Sonuc bulunamadi" demek, kullaniciya aradigi seyin OLMADIGINI
      dusundururdu; oysa burada bilinen tek sey filtrenin eledigi.
    */
    aciklama:
      etkinFiltreSayisi === 1
        ? "Kayıt olabilir ama etkin filtre onu eliyor. Filtreyi kaldırıp tekrar bakın."
        : `Kayıt olabilir ama ${etkinFiltreSayisi} etkin filtre onu eliyor. Filtreleri kaldırıp tekrar bakın.`,
  };
}
